/**
 * WallpaperCategoriesContext — lifts the three category hooks out of individual
 * card components so there is EXACTLY ONE subscription / storage listener for
 * the whole app, no matter how many WallpaperCard / AdminEditableCard instances
 * are mounted simultaneously.
 *
 * Mount <WallpaperCategoriesProvider> high in the tree (App.tsx) and read via
 * useWallpaperCategoriesContext() in any component that previously called the
 * hooks directly.
 */

import React, { createContext, useContext, type ReactNode } from 'react';
import {
  useWallpaperCategoriesFromDB,
  useLiveWallpaperCategoriesFromStorage,
  useRingtomeCategoriesFromStorage,
} from '@/utils/wallpaperCategories';

interface WallpaperCategoriesContextValue {
  wallpaperCats: string[];
  liveCats: string[];
  ringtoneCats: string[];
}

const WallpaperCategoriesContext = createContext<WallpaperCategoriesContextValue | null>(null);

export function WallpaperCategoriesProvider({ children }: { children: ReactNode }) {
  const { categories: wallpaperCats } = useWallpaperCategoriesFromDB();
  const liveCats = useLiveWallpaperCategoriesFromStorage();
  const ringtoneCats = useRingtomeCategoriesFromStorage();

  return (
    <WallpaperCategoriesContext.Provider value={{ wallpaperCats, liveCats, ringtoneCats }}>
      {children}
    </WallpaperCategoriesContext.Provider>
  );
}

export function useWallpaperCategoriesContext(): WallpaperCategoriesContextValue {
  const ctx = useContext(WallpaperCategoriesContext);
  if (!ctx) {
    throw new Error('useWallpaperCategoriesContext must be used inside WallpaperCategoriesProvider');
  }
  return ctx;
}
